<template>
  <b-card class="mb-3">
    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
      <strong>TC{{ committee.number }}.{{ technicalCommittee.number }}</strong>
      <div class="d-flex align-items-center flex-wrap">
        <template v-if="canMove">
          <b-form-select
            :value="moveTarget"
            :options="tcMoveOptions"
            size="sm"
            class="mr-2 mb-1"
            style="min-width: 12rem;"
            @input="$emit('set-move-target', $event)"
          />
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-2 mb-1"
            :disabled="!moveTarget"
            @click="$emit('move', moveTarget)"
          >
            Move
          </b-button>
        </template>
        <b-button
          size="sm"
          variant="outline-danger"
          class="mb-1"
          @click="$emit('remove')"
        >
          Remove
        </b-button>
      </div>
    </div>
    <b-row>
      <b-col cols="12" md="2">
        <b-form-group label="Number">
          <b-form-input
            v-model.number="technicalCommittee.number"
            type="number"
            min="1"
            @change="$emit('sync-number')"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="5">
        <b-form-group label="Name">
          <b-form-input v-model="technicalCommittee.name" />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="5">
        <b-form-group label="Description">
          <b-form-textarea v-model="technicalCommittee.description" rows="2" maxlength="1000" />
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="isNewTechnicalCommittee" class="mb-3">
      <p class="text-muted small mb-2">
        Select existing TCs before or while removing them if this new TC should inherit their conferences.
        Untick a TC to remove it from the selection.
      </p>
      <b-form-group
        label="Inherit main sponsorship from"
        description="Conferences that list these TCs as main sponsors will also list this new TC."
      >
        <div
          v-if="hasInheritSelection('inheritMainSponsorshipFromTcIds')"
          class="d-flex justify-content-end mb-1"
        >
          <b-button
            size="sm"
            variant="link"
            class="p-0"
            @click="clearInherit('inheritMainSponsorshipFromTcIds')"
          >
            Clear selection
          </b-button>
        </div>
        <div class="inherit-source-list border rounded px-2 py-1">
          <b-form-checkbox-group
            v-if="inheritSourceOptions.length"
            v-model="technicalCommittee.inheritMainSponsorshipFromTcIds"
            :options="inheritSourceOptions"
            stacked
          />
          <span v-else class="text-muted small">No existing TCs to inherit from.</span>
        </div>
      </b-form-group>
      <b-form-group
        label="Inherit co-sponsorship from"
        description="Conferences that list these TCs as co-sponsors will also list this new TC."
      >
        <div
          v-if="hasInheritSelection('inheritCoSponsorshipFromTcIds')"
          class="d-flex justify-content-end mb-1"
        >
          <b-button
            size="sm"
            variant="link"
            class="p-0"
            @click="clearInherit('inheritCoSponsorshipFromTcIds')"
          >
            Clear selection
          </b-button>
        </div>
        <div class="inherit-source-list border rounded px-2 py-1">
          <b-form-checkbox-group
            v-if="inheritSourceOptions.length"
            v-model="technicalCommittee.inheritCoSponsorshipFromTcIds"
            :options="inheritSourceOptions"
            stacked
          />
          <span v-else class="text-muted small">No existing TCs to inherit from.</span>
        </div>
      </b-form-group>
    </div>

    <div class="mb-2"><strong>Keywords</strong></div>
    <div
      v-for="(keyword, keywordIndex) in activeKeywords"
      :key="keywordRowKey(keyword, keywordIndex)"
      class="d-flex mb-2 align-items-center flex-wrap"
    >
      <b-form-input
        v-model="keyword.name"
        class="mr-2 mb-1"
        style="min-width: 12rem; flex: 1;"
        @blur="$emit('check-keyword-similarity', keyword)"
      />
      <template v-if="canMoveKeywords">
        <b-form-select
          :value="keywordMoveTargets[keywordRowKey(keyword, keywordIndex)]"
          :options="keywordMoveOptions"
          size="sm"
          class="mr-2 mb-1"
          style="min-width: 12rem;"
          @input="$emit('set-keyword-move-target', {
            keywordKey: keywordRowKey(keyword, keywordIndex),
            target: $event,
          })"
        />
        <b-button
          size="sm"
          variant="outline-primary"
          class="mr-2 mb-1"
          :disabled="!keywordMoveTargets[keywordRowKey(keyword, keywordIndex)]"
          @click="$emit('move-keyword', {
            keywordIndex,
            target: keywordMoveTargets[keywordRowKey(keyword, keywordIndex)],
          })"
        >
          Move
        </b-button>
      </template>
      <b-button
        size="sm"
        variant="outline-danger"
        class="mb-1"
        @click="$emit('remove-keyword', keywordIndex)"
      >
        Remove
      </b-button>
    </div>
    <b-button
      size="sm"
      variant="outline-primary"
      @click="$emit('add-keyword')"
    >
      Add keyword
    </b-button>
  </b-card>
</template>

<script>
export default {
  name: 'TechnicalCommitteeEditor',
  props: {
    committee: {
      type: Object,
      required: true,
    },
    technicalCommittee: {
      type: Object,
      required: true,
    },
    tcKey: {
      type: String,
      required: true,
    },
    tcMoveOptions: {
      type: Array,
      default: () => [],
    },
    keywordMoveOptions: {
      type: Array,
      default: () => [],
    },
    moveTarget: {
      default: null,
    },
    keywordMoveTargets: {
      type: Object,
      default: () => ({}),
    },
    canMove: {
      type: Boolean,
      default: false,
    },
    canMoveKeywords: {
      type: Boolean,
      default: false,
    },
    inheritSourceOptions: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    this.ensureInheritArrays();
  },
  computed: {
    isNewTechnicalCommittee() {
      return !this.technicalCommittee.id;
    },
    activeKeywords() {
      return (this.technicalCommittee.keywords || []).filter((keyword) => !keyword.deleted);
    },
  },
  methods: {
    keywordRowKey(keyword, keywordIndex) {
      return `${this.tcKey}::${String(keyword.id || keyword.draftId || `kw-${keywordIndex}`)}`;
    },
    ensureInheritArrays() {
      if (this.technicalCommittee.id) {
        return;
      }

      ['inheritMainSponsorshipFromTcIds', 'inheritCoSponsorshipFromTcIds'].forEach((field) => {
        const ids = Array.isArray(this.technicalCommittee[field])
          ? this.technicalCommittee[field]
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
          : [];

        this.$set(this.technicalCommittee, field, ids);
      });
    },
    hasInheritSelection(field) {
      return (this.technicalCommittee[field] || []).length > 0;
    },
    clearInherit(field) {
      this.$set(this.technicalCommittee, field, []);
    },
  },
};
</script>

<style scoped>
.inherit-source-list {
  max-height: 12rem;
  overflow-y: auto;
}
</style>
